/**
 * Bundle Discount Code Generator
 * Automatically creates and applies discount codes for bundles
 */

class BundleDiscountAPI {
  constructor(config) {
    this.apiToken = config.apiToken;
    this.shopDomain = config.shopDomain;
    this.apiVersion = '2024-01';
  }

  /**
   * Get discount code for bundle tier (codes must be pre-created in Shopify Admin)
   * @param {Object} bundleData - Bundle information
   * @param {Object} cartData - Optional cart data with actual prices
   */
  async generateDiscountCode(bundleData, cartData = null) {
    const { bundleId, bundleTier, bundleTotal, bundlePrice, items, currency } = bundleData;
    
    // Format bundle group ID for discount code (uppercase, truncate to 20 chars)
    // Note: Matching serverless function format - dashes are preserved
    let bundleGroupId = '';
    if (bundleId) {
      bundleGroupId = bundleId
        .toString()
        .toUpperCase()
        .substring(0, 20); // Truncate to 20 characters max
    }
    
    // Fixed code per tier with bundle group ID for uniqueness (must be pre-created in Shopify Admin)
    // Format: BDL-{QUANTITY}P-{BUNDLE_GROUP_ID}
    const code = bundleGroupId 
      ? `BDL-${bundleTier}P-${bundleGroupId}`
      : `BDL-${bundleTier}P`; // Fallback if no bundleId provided
    
    console.log('Bundle Discount API: Using pre-created discount code:', code, {
      bundleTier,
      bundleId,
      formattedGroupId: bundleGroupId
    });
    console.warn('Bundle Discount API: Note - Discount codes must be pre-created in Shopify Admin. Admin API cannot be called from browser due to CORS restrictions.');
    
    // Return the code (assumes it exists in Shopify Admin)
    return code;
    
    // Calculate discount amount
    let originalTotal = 0;
    const bundleTotalCents = Math.round(bundleTotal * 100);
    
    // Method 1: Use cart data if provided (most accurate)
    if (cartData && cartData.items) {
      cartData.items.forEach(item => {
        // Check if this item is part of the bundle
        if (item.properties && item.properties._bundle_id === bundleId) {
          // Use line_price (total for this line) or calculate from price * quantity
          if (item.original_line_price) {
            originalTotal += item.original_line_price;
          } else if (item.line_price) {
            originalTotal += item.line_price;
          } else if (item.variant && item.variant.price) {
            originalTotal += item.variant.price * item.quantity;
          }
        }
      });
    } else if (items && items.length > 0) {
      // Method 2: Use item variant prices if available
      items.forEach(item => {
        if (item.variant && item.variant.price) {
          // Price is already in dollars, convert to cents
          originalTotal += Math.round(parseFloat(item.variant.price) * 100);
        } else if (item.properties && item.properties._bundle_price) {
          // Method 3: Estimate from bundle price
          // Try to get actual price by fetching variant data
          // For now, use a conservative estimate
          const estimatedOriginal = parseFloat(item.properties._bundle_price) * 1.3; // 30% markup estimate
          originalTotal += Math.round(estimatedOriginal * 100);
        }
      });
    }
    
    // Calculate discount
    const discountAmountCents = originalTotal > 0 ? (originalTotal - bundleTotalCents) : 0;
    
    // Only create discount if there's a positive discount
    if (discountAmountCents <= 0) {
      console.warn('Bundle Discount API: No discount to apply');
      return null;
    }
    
    // Note: Discount codes must be pre-created in Shopify Admin
    // Admin API cannot be called from browser due to CORS restrictions
    // This function now only returns the code name (assumes it exists)
    
    return code;
  }

  /**
   * Find existing discount code by code string
   */
  async findExistingDiscountCode(code) {
    try {
      // Extract tier from code (supports both formats):
      // - BDL-2P (old format)
      // - BDL-2P-LESTRETCHBRA (new format with bundle group ID)
      const tierMatch = code.match(/BDL-(\d+)P(?:-([A-Z0-9-]+))?/);
      const tier = tierMatch ? tierMatch[1] : null;
      const bundleGroupId = tierMatch ? tierMatch[2] : null;
      
      if (!tier) {
        return null;
      }
      
      // Search price rules by title pattern
      const searchTitle = `Bundle Discount - ${tier}-Pack`;
      const priceRulesResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules.json?limit=250`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        }
      });

      if (!priceRulesResponse.ok) {
        return null;
      }

      const priceRulesData = await priceRulesResponse.json();
      
      if (priceRulesData.price_rules && priceRulesData.price_rules.length > 0) {
        // Find price rule with matching title
        const matchingPriceRule = priceRulesData.price_rules.find(pr => 
          pr.title && pr.title.includes(searchTitle)
        );
        
        if (matchingPriceRule) {
          // Get discount codes for this price rule
          const priceRuleId = matchingPriceRule.id;
          const codesResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}/discount_codes.json`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Access-Token': this.apiToken
            }
          });

          if (codesResponse.ok) {
            const codesData = await codesResponse.json();
            if (codesData.discount_codes && codesData.discount_codes.length > 0) {
              const matchingCode = codesData.discount_codes.find(dc => dc.code === code);
              if (matchingCode) {
                return { ...matchingCode, price_rule_id: priceRuleId };
              }
              // If code doesn't match exactly but price rule exists, return the first code
              // (in case code was manually changed)
              if (codesData.discount_codes.length > 0) {
                return { ...codesData.discount_codes[0], price_rule_id: priceRuleId };
              }
            }
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding existing discount code:', error);
      return null;
    }
  }

  /**
   * Get price rule by ID
   */
  async getPriceRule(priceRuleId) {
    try {
      const response = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}.json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.price_rule;
    } catch (error) {
      console.error('Error getting price rule:', error);
      return null;
    }
  }

  /**
   * Update price rule with new discount amount
   */
  async updatePriceRule(priceRuleId, discountAmount) {
    try {
      const response = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          price_rule: {
            value: `-${discountAmount}`
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Bundle Discount API: Price rule update failed', errorData);
        throw new Error(`Price rule update error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating price rule:', error);
      throw error;
    }
  }

  /**
   * Create new discount code and price rule
   */
  async createDiscountCode(code, bundleTier, discountAmount) {
    try {
      // Create price rule (discount)
      const priceRuleResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          price_rule: {
            title: `Bundle Discount - ${bundleTier}-Pack`,
            target_type: 'line_item',
            target_selection: 'all',
            allocation_method: 'across',
            value_type: 'fixed_amount',
            value: `-${discountAmount}`,
            customer_selection: 'all',
            starts_at: new Date().toISOString(),
            ends_at: null,
            usage_limit: null, // No limit - reusable
            once_per_customer: false,
            prerequisite_subtotal_min: null,
            prerequisite_subtotal_max: null,
            prerequisite_quantity_range: null,
            prerequisite_quantity_min: null,
            prerequisite_quantity_max: null,
            prerequisite_shipping_country_code: null,
            prerequisite_product_ids: [],
            prerequisite_variant_ids: [],
            prerequisite_collection_ids: [],
            prerequisite_customer_ids: [],
            entitlement_to_qualifying_quantity_ratio: {
              entitled_quantity: null,
              qualifying_quantity: null
            },
            prerequisite_to_entitlement_quantity_ratio: {
              prerequisite_quantity: null,
              entitled_quantity: null
            },
            prerequisite_to_entitlement_purchase: {
              prerequisite_amount: null
            }
          }
        })
      });

      if (!priceRuleResponse.ok) {
        const errorData = await priceRuleResponse.json().catch(() => ({}));
        console.error('Bundle Discount API: Price rule creation failed', errorData);
        throw new Error(`API error: ${priceRuleResponse.statusText}`);
      }

      const priceRuleData = await priceRuleResponse.json();
      const priceRuleId = priceRuleData.price_rule?.id;

      if (!priceRuleId) {
        throw new Error('Failed to get price rule ID');
      }

      // Create discount code
      const codeResponse = await fetch(`https://${this.shopDomain}/admin/api/${this.apiVersion}/price_rules/${priceRuleId}/discount_codes.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          discount_code: {
            code: code
          }
        })
      });

      if (!codeResponse.ok) {
        const errorData = await codeResponse.json().catch(() => ({}));
        console.error('Bundle Discount API: Code creation failed', errorData);
        throw new Error(`Code creation error: ${codeResponse.statusText}`);
      }

      const codeData = await codeResponse.json();
      console.log(`Bundle Discount API: Created new reusable discount code ${code}`);
      return codeData.discount_code?.code || code;

    } catch (error) {
      console.error('Error generating discount code:', error);
      throw error;
    }
  }

  /**
   * Apply discount code to cart
   */
  async applyDiscountCode(code) {
    console.log('Bundle Discount API: Applying discount code:', code);
    
    try {
      // Method 1: Try using the discount endpoint
      try {
        console.log('Bundle Discount API: Trying discount endpoint method...');
        const response = await fetch('/discount/' + encodeURIComponent(code), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        console.log('Bundle Discount API: Discount endpoint response:', {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText
        });

        if (response.ok) {
          const responseData = await response.json().catch(() => ({}));
          console.log('Bundle Discount API: Discount applied successfully via endpoint', responseData);
          // Success - refresh cart smoothly
          await new Promise(resolve => setTimeout(resolve, 300)); // Small delay for discount to propagate
          this.refreshCart();
          return true;
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.warn('Bundle Discount API: Discount endpoint failed', errorData);
        }
      } catch (e) {
        console.warn('Bundle Discount API: Discount endpoint method failed, trying alternative', e);
      }

      // Method 2: Use cart update with discount code
      try {
        console.log('Bundle Discount API: Trying cart update method...');
        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            discount: code
          })
        });

        console.log('Bundle Discount API: Cart update response:', {
          ok: updateResponse.ok,
          status: updateResponse.status,
          statusText: updateResponse.statusText
        });

        if (updateResponse.ok) {
          const updateData = await updateResponse.json().catch(() => ({}));
          console.log('Bundle Discount API: Discount applied successfully via cart update', updateData);
          
          // Verify discount was actually applied and check discount_applications
          if (updateData.discount_codes && updateData.discount_codes.length > 0) {
            const appliedCode = updateData.discount_codes[0].code;
            console.log('Bundle Discount API: Verified discount code applied:', appliedCode);
            
            // Check if discount is actually working (discount_applications should exist)
            if (updateData.discount_applications && updateData.discount_applications.length > 0) {
              console.log('Bundle Discount API: ✅ Discount is active and applying to items');
              console.log('Bundle Discount API: Discount details:', updateData.discount_applications[0]);
              console.log('Bundle Discount API: Cart total:', updateData.total_price, 'Original:', updateData.original_total_price);
            } else {
              console.warn('Bundle Discount API: ⚠️ Discount code applied but discount_applications is empty');
              console.warn('Bundle Discount API: This means the discount code exists but isn\'t applying to the items');
              console.warn('Bundle Discount API: Possible reasons:');
              console.warn('  1. Items in cart are not in the collection specified by the discount');
              console.warn('  2. Discount code doesn\'t exist in Shopify (check Admin → Discounts)');
              console.warn('  3. Discount conditions are not met (quantity, collection, etc.)');
              console.warn('Bundle Discount API: Cart items:', updateData.items?.map(i => ({
                product: i.product_title,
                variant: i.variant_id
              })) || 'no items');
            }
          }
          
          await new Promise(resolve => setTimeout(resolve, 800)); // Delay for discount to propagate
          this.refreshCart();
          return true;
        } else {
          const errorData = await updateResponse.json().catch(() => ({}));
          console.warn('Bundle Discount API: Cart update failed', errorData);
        }
      } catch (e) {
        console.warn('Bundle Discount API: Cart update method failed, trying form submission', e);
      }

      // Method 3: Use cart attributes (alternative method)
      try {
        console.log('Bundle Discount API: Trying cart attributes method...');
        const attributesResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            attributes: {
              '_discount_code': code
            }
          })
        });

        if (attributesResponse.ok) {
          console.log('Bundle Discount API: Discount code stored in cart attributes');
          // Still need to apply it
        }
      } catch (e) {
        console.warn('Bundle Discount API: Cart attributes method failed', e);
      }

      // Method 4: Redirect to cart with discount code (fallback)
      // This will show the discount code input field pre-filled
      console.log('Bundle Discount API: All methods failed, redirecting to cart with discount code');
      window.location.href = '/cart?discount=' + encodeURIComponent(code);
      return true;

    } catch (error) {
      console.error('Bundle Discount API: Error applying discount code:', error);
      // Fallback: redirect to cart page where user can manually enter code
      window.location.href = '/cart?discount=' + encodeURIComponent(code);
      return false;
    }
  }

  /**
   * Remove discount code from cart
   */
  async removeDiscountCode(code) {
    console.log('Bundle Discount API: Removing discount code:', code);
    
    try {
      // Method 1: Clear discount via cart update
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount: '' // Empty string removes discount
        })
      });

      if (response.ok) {
        console.log('Bundle Discount API: Discount code removed successfully');
        this.refreshCart();
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.warn('Bundle Discount API: Failed to remove discount code', errorData);
        return false;
      }
    } catch (error) {
      console.error('Bundle Discount API: Error removing discount code:', error);
      return false;
    }
  }

  /**
   * Refresh cart display without page reload
   */
  refreshCart() {
    // Try to use cart-items custom element's update method
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.updateCartAfterGrouping === 'function') {
      cartItems.updateCartAfterGrouping();
    } else if (cartItems && typeof cartItems.renderItems === 'function') {
      cartItems.renderItems();
    } else if (typeof window.theme !== 'undefined' && window.theme.cartDrawer) {
      window.theme.cartDrawer.refresh();
    } else if (typeof window.theme !== 'undefined' && window.theme.cart) {
      window.theme.cart.refresh();
    } else {
      // Last resort: reload page only if no other method works
      console.warn('Bundle Discount API: No cart refresh method available, reloading page');
      window.location.reload();
    }
  }

}

// Initialize when DOM is ready
(function() {
  'use strict';

  // Wait for DOM and theme settings
  function initBundleDiscountAPI() {
    console.log('Bundle Discount API: Initializing...');
    
    // Get shop domain from config (API token not needed - codes are pre-created by serverless function)
    const shopDomain = window.bundleDiscountConfig?.shopDomain || window.Shopify?.shop || 'sheer-2.myshopify.com';

    console.log('Bundle Discount API: Initializing (codes are pre-created by serverless function)', {
      hasConfig: !!window.bundleDiscountConfig,
      shopDomain: shopDomain
    });

    // Initialize without API token - we only apply pre-created codes, not create them
    const discountAPI = new BundleDiscountAPI({
      apiToken: null, // Not needed - codes are pre-created
      shopDomain: shopDomain
    });

    // Expose globally for use in bundle modal
    window.bundleDiscountAPI = discountAPI;
    
    console.log('Bundle Discount API: Initialized successfully', discountAPI);

    return discountAPI;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleDiscountAPI);
  } else {
    initBundleDiscountAPI();
  }
})();

